import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { AlertCircle, CheckCircle2, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2Settings() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSettings = async () => {
    try {
      const [sRes, vRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/settings`, { headers }),
        axios.post(`${API_URL}/api/v2/settings/validate`, {}, { headers })
      ]);
      setSettings(sRes.data);
      setValidation(vRes.data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/v2/settings`, settings, { headers });
      toast.success('Settings saved');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading || !settings) return <DashboardLayout role="super_admin"><div className="p-8 text-center text-muted-foreground">Loading settings...</div></DashboardLayout>;

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6 max-w-4xl" data-testid="v2-settings">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">V2 Interview Settings</h1>
          <Button onClick={handleSave} disabled={saving} data-testid="v2-save-settings">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Validation Banner */}
        {validation && (
          <div className={`p-4 rounded-lg border ${validation.valid ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'}`}>
            <div className="flex items-center gap-2 mb-2">
              {validation.valid ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
              <span className="font-medium">{validation.valid ? 'Configuration Valid' : 'Configuration Issues'}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {validation.exact_question_count} questions × {validation.per_question_seconds}s = {validation.total_question_minutes} min / {validation.max_interview_minutes} min ({validation.remaining_minutes} min remaining)
            </p>
            {validation.issues?.map((issue, i) => <p key={i} className="text-sm text-amber-700 dark:text-amber-400 mt-1">{issue}</p>)}
          </div>
        )}

        {/* Question Count & Timing */}
        <Card>
          <CardHeader><CardTitle className="text-base">Question Count & Timing</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Exact Question Count</label>
                <Input type="number" min={1} max={100} value={settings.exact_question_count} onChange={e => updateField('exact_question_count', parseInt(e.target.value) || 1)} data-testid="v2-question-count" />
                <p className="text-xs text-muted-foreground mt-1">Every interview will contain exactly this many questions</p>
              </div>
              <div>
                <label className="text-sm font-medium">Max Interview Duration (minutes)</label>
                <Input type="number" min={1} max={180} value={settings.max_interview_duration_minutes} onChange={e => updateField('max_interview_duration_minutes', parseInt(e.target.value) || 45)} />
              </div>
              <div>
                <label className="text-sm font-medium">Reading Time per Question (seconds)</label>
                <Input type="number" min={10} max={300} value={settings.reading_time_per_question} onChange={e => updateField('reading_time_per_question', parseInt(e.target.value) || 60)} />
              </div>
              <div>
                <label className="text-sm font-medium">Answer Time per Question (seconds)</label>
                <Input type="number" min={10} max={600} value={settings.answering_time_per_question} onChange={e => updateField('answering_time_per_question', parseInt(e.target.value) || 90)} />
              </div>
              <div>
                <label className="text-sm font-medium">Transition Buffer (seconds)</label>
                <Input type="number" min={0} max={30} value={settings.transition_buffer_seconds} onChange={e => updateField('transition_buffer_seconds', parseInt(e.target.value) || 5)} />
              </div>
              <div>
                <label className="text-sm font-medium">Passing Percentage</label>
                <Input type="number" min={0} max={100} value={settings.passing_percentage} onChange={e => updateField('passing_percentage', parseInt(e.target.value) || 50)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Behaviour */}
        <Card>
          <CardHeader><CardTitle className="text-base">Interview Behaviour</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { field: 'allow_early_answer', label: 'Allow Early Answer Submission' },
                { field: 'allow_early_read_complete', label: 'Allow Early Reading Completion' },
                { field: 'question_randomisation', label: 'Question Randomisation' },
                { field: 'auto_complete_after_final', label: 'Auto-Complete After Final Question' },
                { field: 'interview_resume_enabled', label: 'Allow Interview Resume' },
                { field: 'ai_answer_evaluation', label: 'AI Answer Evaluation' },
                { field: 'proctoring_enabled', label: 'Proctoring' },
                { field: 'video_recording_enabled', label: 'Video Recording' },
              ].map(({ field, label }) => (
                <label key={field} className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50">
                  <input type="checkbox" checked={settings[field] !== false} onChange={e => updateField(field, e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Repeat Policy */}
        <Card>
          <CardHeader><CardTitle className="text-base">Repeated Question Policy</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { value: 'strict_no_repeat', label: 'Strict — Never reuse previously seen questions', desc: 'Blocks interview creation if insufficient unseen questions exist' },
                { value: 'prefer_unseen', label: 'Flexible — Prefer unseen, allow older if necessary', desc: 'Logs when previously used questions are selected' },
              ].map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer ${settings.repeated_question_policy === opt.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                  <input type="radio" name="repeat_policy" checked={settings.repeated_question_policy === opt.value} onChange={() => updateField('repeated_question_policy', opt.value)} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
